import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, CircularProgress, Divider, IconButton, Link, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { SBSection, SourceBook } from '@dndModels';
import API from '@utilities/smart_api';
import ModalOrDrawer from '@components/display/ModalOrDrawer';
import SBSectionContentViewer from './SBSectionContentViewer';

const api = API.getInstance();

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function flatten(sections: SBSection[], depth = 0): { section: SBSection; depth: number }[] {
  const result: { section: SBSection; depth: number }[] = [];
  for (const s of sections) {
    result.push({ section: s, depth });
    result.push(...flatten(s.subsections, depth + 1));
  }
  return result;
}

function getAncestorAndSelfIds(
  nodes: { section: SBSection; depth: number }[],
  targetId: string
): Set<string> {
  const idx = nodes.findIndex(n => n.section.true_id === targetId);
  if (idx === -1) return new Set();
  const ids = new Set<string>([targetId]);
  let depth = nodes[idx].depth;
  for (let i = idx - 1; i >= 0 && depth > 0; i--) {
    if (nodes[i].depth < depth) {
      ids.add(nodes[i].section.true_id);
      depth = nodes[i].depth;
    }
  }
  return ids;
}

/**
 * Walk up the flat node list from `idx` to find the nearest ancestor (or self)
 * that is a single_page root — i.e. the section that should actually be
 * displayed when any of its single_page descendants are selected.
 */
function getSinglePageRoot(
  nodes: { section: SBSection; depth: number }[],
  idx: number
): { section: SBSection; idx: number } {
  // Walk up: if any ancestor has single_page=true, that ancestor is the root to show.
  // We keep walking until we find the topmost single_page ancestor.
  let rootIdx = idx;
  let depth = nodes[idx].depth;
  for (let i = idx - 1; i >= 0 && depth > 0; i--) {
    if (nodes[i].depth < depth) {
      if (nodes[i].section.single_page) {
        rootIdx = i;
      }
      depth = nodes[i].depth;
    }
  }
  return { section: nodes[rootIdx].section, idx: rootIdx };
}

function getChildIds(
  nodes: { section: SBSection; depth: number }[],
  idx: number
): string[] {
  const parentDepth = nodes[idx].depth;
  const ids: string[] = [];
  for (let i = idx + 1; i < nodes.length; i++) {
    if (nodes[i].depth <= parentDepth) break;
    if (nodes[i].depth === parentDepth + 1) ids.push(nodes[i].section.true_id);
  }
  return ids;
}

/**
 * Special sections generate their subsections from the source book's other objects,
 * except Contents (which lists the real sections) and the ones that are a single table.
 */
function generatesSubsections(section: SBSection): boolean {
  return !['None', 'Contents', 'Weapon Table', 'Armor Table'].includes(section.special_type);
}

// ---------------------------------------------------------------------------
// TreeNode
// ---------------------------------------------------------------------------

interface TreeNodeProps {
  nodes: { section: SBSection; depth: number }[];
  idx: number;
  expandedIds: Set<string>;
  selectedId: string | null;
  // A special section whose subsections haven't been generated yet.
  unbuilt: boolean;
  building: boolean;
  onToggleExpand: (id: string, idx: number) => void;
  onSelect: (id: string, idx: number) => void;
}

const TreeNode = React.memo(function TreeNode({
  nodes, idx, expandedIds, selectedId, unbuilt, building, onToggleExpand, onSelect,
}: TreeNodeProps) {
  const { section, depth } = nodes[idx];
  const tid = section.true_id;
  const expanded = expandedIds.has(tid);
  const selected = selectedId === tid;

  // Hide if any ancestor is collapsed
  if (depth > 0) {
    let j = idx - 1;
    while (j >= 0 && nodes[j].depth >= depth) j--;
    if (j >= 0 && !expandedIds.has(nodes[j].section.true_id)) return null;
    let ancestor = j;
    while (ancestor > 0) {
      let k = ancestor - 1;
      while (k >= 0 && nodes[k].depth >= nodes[ancestor].depth) k--;
      if (k < 0) break;
      if (!expandedIds.has(nodes[k].section.true_id)) return null;
      ancestor = k;
    }
  }

  const childCount = getChildIds(nodes, idx).length;

  return (
    <div style={{ marginLeft: depth * 16 }}>
      <div
        onClick={() => onSelect(tid, idx)}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '3px 6px',
          borderRadius: 4,
          cursor: 'pointer',
          backgroundColor: selected ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
          fontWeight: selected ? 600 : 400,
          userSelect: 'none',
        }}
      >
        <span style={{ flex: 1, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {section.name || <em style={{ color: '#888' }}>Untitled Section</em>}
        </span>
        {building ? (
          <CircularProgress size={16} style={{ margin: 4 }} />
        ) : (childCount > 0 || unbuilt) && (
          <IconButton
            size="small"
            onClick={e => { e.stopPropagation(); onToggleExpand(tid, idx); }}
            style={{ padding: 2 }}
          >
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        )}
      </div>
    </div>
  );
});

// ---------------------------------------------------------------------------
// Pages and paths
// ---------------------------------------------------------------------------

type FlatNode = { section: SBSection; depth: number };

// What a section is called in the url: "Rhythm of Play" is "rhythm-of-play".
function slugOf(name: string): string {
  const slug = name.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return slug === "" ? "section" : slug;
}

// The slugs for a list of sibling sections, with -2, -3, ... added to repeats so each one is unique.
function siblingSlugs(siblings: SBSection[]): string[] {
  const seen: { [slug: string]: number } = {};
  return siblings.map(s => {
    const base = slugOf(s.name);
    seen[base] = (seen[base] ?? 0) + 1;
    return seen[base] === 1 ? base : `${base}-${seen[base]}`;
  });
}

// The url path to a section, like "spells/spell-descriptions/a/acid-arrow". "" if it isn't in the tree.
function pathOf(roots: SBSection[], target: SBSection): string {
  const search = (siblings: SBSection[], prefix: string[]): string[] | null => {
    const slugs = siblingSlugs(siblings);
    for (let i = 0; i < siblings.length; i++) {
      const path = [...prefix, slugs[i]];
      if (siblings[i] === target) return path;
      const found = search(siblings[i].subsections, path);
      if (found) return found;
    }
    return null;
  };
  return (search(roots, []) ?? []).join("/");
}

// Scrolls a section on the page into view, once React has committed it.
// (A timeout rather than requestAnimationFrame, which doesn't run while the window isn't being drawn.)
function scrollToSection(section: SBSection) {
  setTimeout(() => {
    document.getElementById(`sb-section-${section.true_id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 50);
}

// What gets shown one at a time, in book order: every section, except those shown
// as part of a single page section they're under.
function pagesOf(nodes: FlatNode[]): FlatNode[] {
  return nodes.filter((_, i) => getSinglePageRoot(nodes, i).idx === i);
}

// ---------------------------------------------------------------------------
// SBSectionsViewer
// ---------------------------------------------------------------------------

interface SBSectionsViewerProps {
  sections: SBSection[];
  source_book: SourceBook;
  // The page being read, as a path of section slugs ("" for the section menu).
  page_path: string;
  onNavigate: (page_path: string) => void;
}

/**
 * Reads a source book. With nothing selected it shows the section menu. Once a page is
 * selected it shows just that page, with buttons to go to the previous and next pages and
 * to bring up the section menu. Which page is shown comes from page_path (the url).
 */
export default function SBSectionsViewer({ sections, source_book, page_path, onNavigate }: SBSectionsViewerProps) {
  // The section tree this viewer works from. Kept from mount, because the source book's
  // sections array can be swapped out from under it (a load finishing late copies in new
  // section objects), and generated subsections have to land on the objects being shown.
  const [roots] = useState(sections);
  const [nodes, setNodes] = useState<FlatNode[]>(() => flatten(roots));
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // Special sections' subsection builds, done or in progress, by true_id.
  const builds = useRef<Map<string, Promise<void>>>(new Map());
  const [buildingIds, setBuildingIds] = useState<Set<string>>(new Set());
  const [menuOpen, setMenuOpen] = useState(false);
  // page_path worked out into the section to show. Undefined while it's being worked out.
  const [shown, setShown] = useState<{ path: string, page: SBSection | null } | undefined>(undefined);
  // When the path points into a single page section, the part of the page to scroll to.
  const scrollTargetRef = useRef<SBSection | null>(null);
  // The page currently shown, so going somewhere else on the same page just scrolls there.
  const shownPageRef = useRef<SBSection | null>(null);
  const topRef = useRef<HTMLDivElement | null>(null);
  // The page's content scrolls in here, under the buttons at the top, so they're always visible.
  const contentRef = useRef<HTMLDivElement | null>(null);
  // The height for the page view, so it fills the window from where it starts.
  const [pageHeight, setPageHeight] = useState<number | null>(null);

  // Back to the top of the page: the page view itself, and the content scrolling under the buttons.
  const scrollToTop = useCallback(() => {
    topRef.current?.scrollIntoView({ block: 'start' });
    contentRef.current?.scrollTo({ top: 0 });
  }, []);

  /**
   * Special sections generate their subsections the first time they're needed, so the data
   * types they need only get loaded once someone actually looks at them.
   */
  const buildSection = useCallback((section: SBSection): Promise<void> => {
    const tid = section.true_id;
    if (!generatesSubsections(section)) return Promise.resolve();
    const existing = builds.current.get(tid);
    if (existing) return existing;
    setBuildingIds(prev => new Set(prev).add(tid));
    const promise = (async () => {
      try {
        section.subsections = await api.getSubsectionsForSpecialSection(section, source_book);
        setNodes(flatten(roots));
      } catch (ex) {
        // Let opening it again retry.
        builds.current.delete(tid);
        console.error(ex);
      } finally {
        setBuildingIds(prev => { const next = new Set(prev); next.delete(tid); return next; });
      }
    })();
    builds.current.set(tid, promise);
    return promise;
  }, [roots, source_book]);

  // Works out page_path into the section it points to, building special sections along the way.
  useEffect(() => {
    let active = true;
    const resolve = async () => {
      const slugs = page_path.split("/").filter(s => s !== "");
      let siblings = roots;
      let found: SBSection | null = null;
      for (let i = 0; i < slugs.length; i++) {
        const index = siblingSlugs(siblings).indexOf(slugs[i]);
        if (index === -1) return null;
        found = siblings[index];
        if (i < slugs.length - 1) {
          await buildSection(found);
          siblings = found.subsections;
        }
      }
      return found;
    };
    if (page_path === "") {
      return;
    }
    resolve().then(target => {
      if (!active) return;
      if (!target) {
        shownPageRef.current = null;
        setShown({ path: page_path, page: null });
        return;
      }
      const flat = flatten(roots);
      const page = getSinglePageRoot(flat, flat.findIndex(n => n.section === target)).section;
      // Opening a special section builds it, so its first generated subsection comes next.
      buildSection(page);
      const same_page = shownPageRef.current === page;
      shownPageRef.current = page;
      setShown({ path: page_path, page });
      // Open the menu to where this page is.
      setExpandedIds(prev => {
        const next = new Set(prev);
        getAncestorAndSelfIds(flat, target.true_id).forEach(id => next.add(id));
        return next;
      });
      if (target === page) {
        scrollTargetRef.current = null;
        scrollToTop();
      } else if (same_page) {
        // It's already showing, so its parts are on the page to scroll to.
        scrollTargetRef.current = null;
        scrollToSection(target);
      } else {
        // A part of a single page section: scroll to it once the page has loaded (see handleAllLoaded).
        scrollTargetRef.current = target;
        scrollToTop();
      }
    });
    return () => { active = false; };
  }, [page_path, roots, buildSection, scrollToTop]);

  // Sizes the page view to fill the window below wherever it starts (under the nav bar, etc.).
  const measurePageView = useCallback(() => {
    const el = topRef.current;
    if (el) {
      const top_in_document = el.getBoundingClientRect().top + window.scrollY;
      setPageHeight(Math.max(300, window.innerHeight - top_in_document - 8));
    }
  }, []);
  const showing_page = !!shown?.page;
  useEffect(() => {
    if (!showing_page) return;
    measurePageView();
    window.addEventListener('resize', measurePageView);
    return () => window.removeEventListener('resize', measurePageView);
  }, [showing_page, measurePageView]);

  const goTo = useCallback((section: SBSection) => {
    setMenuOpen(false);
    onNavigate(pathOf(roots, section));
  }, [roots, onNavigate]);

  const toggleExpand = useCallback((id: string, idx: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    buildSection(nodes[idx].section);
  }, [nodes, buildSection]);

  const selectFromMenu = useCallback((_tid: string, idx: number) => {
    goTo(nodes[idx].section);
  }, [nodes, goTo]);

  // Contents sections link to sections by true_id.
  const navigateToSection = useCallback((tid: string) => {
    const node = nodes.find(n => n.section.true_id === tid);
    if (node) {
      goTo(node.section);
    }
  }, [nodes, goTo]);

  /**
   * Called by SBSectionContentViewer once the page and everything on it is loaded,
   * so a path into a single page section can scroll to its part of the page.
   */
  const handleAllLoaded = useCallback(() => {
    const target = scrollTargetRef.current;
    if (!target) return;
    scrollTargetRef.current = null;
    scrollToSection(target);
  }, []);

  const renderMenu = () => (
    <div style={{ padding: '4px' }}>
      {nodes.map((node, i) => (
        <TreeNode
          key={node.section.true_id}
          nodes={nodes}
          idx={i}
          expandedIds={expandedIds}
          selectedId={page_path !== "" ? shown?.page?.true_id ?? null : null}
          unbuilt={generatesSubsections(node.section) && !builds.current.has(node.section.true_id)}
          building={buildingIds.has(node.section.true_id)}
          onToggleExpand={toggleExpand}
          onSelect={selectFromMenu}
        />
      ))}
    </div>
  );

  if (nodes.length === 0) {
    return <span style={{ color: '#888' }}>No sections.</span>;
  }

  // Nothing selected: the section menu.
  if (page_path === "") {
    return (
      <div ref={topRef}>
        <Typography variant="h5">{source_book.name}</Typography>
        <Typography variant="body2" style={{ color: '#888' }}>Choose a section to start reading.</Typography>
        <Divider style={{ margin: '8px 0' }} />
        {renderMenu()}
      </div>
    );
  }

  // While a new path is being worked out, the page that's showing stays up, so going somewhere
  // else on the same page (a single page section's subsection) doesn't unmount and reload it.
  const resolving = !shown || shown.path !== page_path;
  if (resolving && !shown?.page) {
    return (
      <div ref={topRef} style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#888' }}>
        <CircularProgress size={16} />
        <Typography variant="body2">Loading…</Typography>
      </div>
    );
  }

  if (!shown || !shown.page) {
    return (
      <div ref={topRef}>
        <Typography variant="body1" gutterBottom>That page couldn&apos;t be found in {source_book.name}. Choose a section:</Typography>
        <Divider style={{ margin: '8px 0' }} />
        {renderMenu()}
      </div>
    );
  }

  const page = shown.page;
  const pages = pagesOf(nodes);
  const page_index = pages.findIndex(n => n.section === page);
  const previous = page_index > 0 ? pages[page_index - 1].section : null;
  const next = page_index >= 0 && page_index < pages.length - 1 ? pages[page_index + 1].section : null;
  // While this page's subsections are being generated, which page is next isn't known yet.
  const building_page = buildingIds.has(page.true_id);

  const renderPageButtons = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        {previous &&
          <Button size="small" startIcon={<NavigateBeforeIcon />} onClick={() => goTo(previous)}
            style={{ maxWidth: '100%', textTransform: 'none' }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{previous.name || 'Untitled Section'}</span>
          </Button>
        }
      </div>
      <Button size="small" variant="outlined" startIcon={<MenuBookIcon />} onClick={() => setMenuOpen(true)}>
        Sections
      </Button>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>
        {building_page
          ? <CircularProgress size={16} style={{ margin: 8 }} />
          : next &&
            <Button size="small" endIcon={<NavigateNextIcon />} onClick={() => goTo(next)}
              style={{ maxWidth: '100%', textTransform: 'none' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{next.name || 'Untitled Section'}</span>
            </Button>
        }
      </div>
    </div>
  );

  // Links to the page's subsections. On a single page section they're all on this page,
  // so the links scroll to them (with the url still saying which one).
  const renderSubsectionLinks = () => page.subsections.length === 0 ? null : (
    <div style={{ marginBottom: 16 }}>
      <Typography variant="subtitle2" style={{ color: '#888' }}>In this section</Typography>
      <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: 16, rowGap: 2 }}>
        {page.subsections.map(sub => (
          <Link key={sub.true_id} component="button" variant="body2" style={{ textAlign: 'left' }} onClick={() => goTo(sub)}>
            {sub.name || 'Untitled Section'}
          </Link>
        ))}
      </div>
      <Divider style={{ marginBottom: 16 }} />
    </div>
  );

  return (
    <div ref={topRef} style={{ display: 'flex', flexDirection: 'column', height: pageHeight ?? undefined }}>
      <div style={{ flex: 'none' }}>
        {renderPageButtons()}
        <Divider style={{ margin: '4px 0 0' }} />
      </div>
      <div>
        <Typography variant="h5" gutterBottom>
          {page.name || <em style={{ color: '#888' }}>Untitled Section</em>}
        </Typography>
        <Divider style={{ marginBottom: 16 }} />
        {renderSubsectionLinks()}
      </div>
      <div ref={contentRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingTop: 16 }}>
        <SBSectionContentViewer
          key={page.true_id}
          section={page}
          inSinglePage={page.single_page}
          onAllLoaded={handleAllLoaded}
          allSections={roots}
          onNavigate={navigateToSection}
          sourceBookId={source_book._id}
        />
        <Divider style={{ margin: '16px 0 4px' }} />
        {renderPageButtons()}
      </div>
      <ModalOrDrawer open={menuOpen} onClose={() => setMenuOpen(false)}>
        <div style={{ padding: 8 }}>
          <Typography variant="subtitle2" style={{ color: '#555' }}>{source_book.name}</Typography>
          <Divider style={{ marginTop: 4 }} />
          {renderMenu()}
        </div>
      </ModalOrDrawer>
    </div>
  );
}
